import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, userFlagOff } from '../../Redux/Actions/userAction';
import { ReactComponent as DigntecLogo } from '../../Assets/images/DigntecLogo.svg';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TvIcon from '@mui/icons-material/Tv';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DevicesIcon from '@mui/icons-material/Devices';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import './login.css';

function Login() {
  const dispatch = useDispatch();
  const { auth, loginSuccess, failed, isLogging } = useSelector(s => s.User);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    if (loginSuccess) { toast.success('로그인 성공!'); dispatch(userFlagOff()); }
    if (failed)       { toast.error('로그인 실패. 이메일/비밀번호를 확인하거나 관리자에게 계정 승인을 요청하세요.'); dispatch(userFlagOff()); }
  }, [loginSuccess, failed]);

  useEffect(() => {
    const saved = Cookies.get('leedoitCredential');
    if (saved) {
      try {
        const d = JSON.parse(saved);
        setEmail(d?.email || '');
        setPassword(d?.password || '');
        setRememberMe(true);
      } catch (_) {}
    }
  }, []);

  const handleRemember = (e) => {
    if (e.target.checked) {
      setRememberMe(true);
      Cookies.set('leedoitCredential', JSON.stringify({ email, password }), { expires: 7 });
    } else {
      setRememberMe(false);
      Cookies.remove('leedoitCredential');
    }
  };

  const submitLogin = () => {
    if (!email || !password) return;
    dispatch(login(email, password));
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') submitLogin(); };

  if (auth) return <Navigate to="/" />;

  return (
    <div className="lp-root">
      {/* Loading overlay */}
      {isLogging && (
        <div className="lp-overlay">
          <img src="/images/spiner.gif" alt="loading" className="lp-spinner" />
        </div>
      )}

      {/* Left panel — branding */}
      <div className="lp-left">
        <div className="lp-brand">
          <div className="lp-brand-logo">
            <DigntecLogo style={{ width: '160px', height: 'auto', filter: 'brightness(0) invert(1)' }} />
          </div>
          <h1 className="lp-brand-title">스마트 디지털<br />사이니지 관리</h1>
          <p className="lp-brand-sub">
            Android 디바이스를 웹에서 원격으로 관리하세요.<br />
            메뉴보드부터 회사 알림까지 중앙에서 통합 제어합니다.
          </p>

          <div className="lp-features">
            <div className="lp-feature-item">
              <div className="lp-feature-icon"><RestaurantMenuIcon /></div>
              <div>
                <div className="lp-feature-title">디지털 메뉴보드</div>
                <div className="lp-feature-desc">시즌 메뉴, 가격을 실시간으로 업데이트</div>
              </div>
            </div>
            <div className="lp-feature-item">
              <div className="lp-feature-icon"><NotificationsActiveIcon /></div>
              <div>
                <div className="lp-feature-title">알림 & 공지 관리</div>
                <div className="lp-feature-desc">캠페인 및 공지사항을 원격 배포</div>
              </div>
            </div>
            <div className="lp-feature-item">
              <div className="lp-feature-icon"><DevicesIcon /></div>
              <div>
                <div className="lp-feature-title">다중 디바이스 제어</div>
                <div className="lp-feature-desc">여러 매장 화면을 한 곳에서 통합 관리</div>
              </div>
            </div>
            <div className="lp-feature-item">
              <div className="lp-feature-icon"><TvIcon /></div>
              <div>
                <div className="lp-feature-title">실시간 콘텐츠 배포</div>
                <div className="lp-feature-desc">이미지, 영상, URL을 즉시 전송</div>
              </div>
            </div>
          </div>

          {/* decorative screens illustration */}
          <div className="lp-screens">
            <div className="lp-screen lp-screen--lg">
              <div className="lp-screen-content">
                <div className="lp-screen-bar" style={{ width: '70%', background: 'rgba(255,255,255,0.7)' }} />
                <div className="lp-screen-bar" style={{ width: '50%', background: 'rgba(255,255,255,0.4)' }} />
                <div className="lp-screen-bar" style={{ width: '60%', background: 'rgba(255,255,255,0.4)' }} />
              </div>
            </div>
            <div className="lp-screen lp-screen--sm">
              <div className="lp-screen-content">
                <div className="lp-screen-bar" style={{ width: '80%', background: 'rgba(255,255,255,0.7)' }} />
                <div className="lp-screen-bar" style={{ width: '60%', background: 'rgba(255,255,255,0.4)' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="lp-right">
        <div className="lp-form-wrap">
          <div className="lp-form-header">
            <h2 className="lp-form-title">로그인</h2>
            <p className="lp-form-subtitle">계정 정보를 입력하세요</p>
          </div>

          <div className="lp-field">
            <label className="lp-label">이메일</label>
            <input
              className="lp-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="example@email.com"
              autoComplete="email"
            />
          </div>

          <div className="lp-field">
            <label className="lp-label">비밀번호</label>
            <div className="lp-input-wrap">
              <input
                className="lp-input"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="비밀번호 입력"
                autoComplete="current-password"
              />
              <button className="lp-pw-toggle" type="button" onClick={() => setShowPw(v => !v)}>
                {showPw ? <VisibilityOffIcon sx={{ fontSize: 20 }} /> : <VisibilityIcon sx={{ fontSize: 20 }} />}
              </button>
            </div>
          </div>

          <div className="lp-row">
            <label className="lp-checkbox-label">
              <input type="checkbox" checked={rememberMe} onChange={handleRemember} />
              <span>로그인 유지</span>
            </label>
            <Link to="/forgetpassword" className="lp-forgot">비밀번호 찾기</Link>
          </div>

          <button
            className="lp-btn-login"
            onClick={submitLogin}
            disabled={!email || !password || isLogging}
          >
            {isLogging ? '로그인 중...' : '로그인'}
          </button>

          <p className="lp-support">
            문의사항이 있으신가요?{' '}
            <a href="mailto:support@digntec.com">support@digntec.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
